import React, { useEffect, useState } from 'react';

// Fallbacks only; all three are served on every block. Coerced through
// `Number()` at every use because the runner derives its reaction-time ceiling
// as `timing().windowMs + 250`: a string window would concatenate
// ("5000" + 250 -> "5000250") and every late response would be accepted as a
// real one. `||` rather than `??` so a served 0 falls back to the default
// instead of becoming an instant timeout — and, for `delay_ms`, so a served 0
// cannot silently delete the retention interval this instrument is built on.
const DEFAULT_STUDY_ITEM_MS = 2000;
const DEFAULT_DELAY_MS = 20000;
const DEFAULT_WINDOW_MS = 5000;

// The starting number for the filler task. Not a wire parameter and not a
// measurement parameter — the filler only has to occupy the retention
// interval, and nothing is scored from what is typed into it.
const COUNT_FROM = 300;

// ONE style object, shared by the study words and by the item under judgement.
// It is a module constant rather than a function of the word, so no rendered
// attribute can vary with which word is on screen. Studied items and lures come
// from one shuffled pool precisely so that no surface feature separates them;
// any styling that varied by word would put that feature back.
const wordStyle = {
  fontSize: 'clamp(40px, 11vw, 88px)',
  fontWeight: 700,
  letterSpacing: '-0.01em',
  color: 'var(--text-primary)',
};

const wordListOf = (value) =>
  Array.isArray(value) ? value.filter((w) => typeof w === 'string' && w.length > 0) : [];

// Shows one word at a time for study_item_ms each. This is the ONLY place a
// study list may ever be rendered. The View is mounted by the preamble pipeline
// and unmounted by RunnerShell the moment `preamblePhase` moves on, so its
// lifetime is exactly the phase's. It takes NO props: RunnerShell passes
// `params` to every phase View, and `params` carries `study_list` — a View that
// never destructures it structurally cannot redisplay it later by accident.
// The words it does show reach it only through this closure, which nothing
// after the phase holds a reference to.
function studyPhase(id, words, itemMs) {
  const View = () => {
    const [i, setI] = useState(0);
    useEffect(() => {
      if (i >= words.length) return undefined;
      const t = setTimeout(() => setI((n) => n + 1), itemMs);
      return () => clearTimeout(t);
    }, [i]);
    return (
      <div style={{ display: 'grid', gap: 'var(--space-3)', justifyItems: 'center' }}>
        <span
          style={{
            fontSize: 'var(--text-numeric-size)',
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'var(--text-tertiary)',
          }}
        >
          Remember these
        </span>
        {/* Once the queue drains this renders an empty string, never a word:
            the phase's own end and the last word's expiry land on the same
            tick, and whichever wins the race must not put a word back. */}
        <span style={wordStyle}>{words[i] ?? ''}</span>
      </div>
    );
  };
  return { id, durationMs: itemMs * words.length, View };
}

// A FILLED delay, not an empty one. An empty delay lets the participant
// rehearse the list, which is precisely what the filler prevents — and
// rehearsal turns episodic recognition into maintained short-term memory.
// There is deliberately no skip button and no early exit: delay_ms is a
// measurement parameter, not a loading spinner, and shortening it on a fast
// device produces a score that looks entirely normal.
//
// Takes no props, for the same structural reason as studyPhase's View: it is
// handed `params` by RunnerShell, `params` contains the answer key, and a
// component that never names it cannot render it.
function delayPhase(delayMs) {
  const View = () => {
    const [entries, setEntries] = useState([]);
    const [value, setValue] = useState('');
    return (
      <div style={{ display: 'grid', gap: 'var(--space-4)', justifyItems: 'center', maxWidth: 420 }}>
        <span
          style={{
            fontSize: 'var(--text-panel-size)',
            fontWeight: 700,
            color: 'var(--text-primary)',
            textAlign: 'center',
          }}
        >
          Count backwards by threes from {COUNT_FROM}. Type each number.
        </span>
        <input
          type="text"
          inputMode="numeric"
          aria-label="Count backwards by threes"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            setEntries((prev) => [...prev, value]);
            setValue('');
          }}
          style={{
            fontSize: 'var(--text-page-size)',
            padding: 'var(--space-3) var(--space-4)',
            width: 160,
            textAlign: 'center',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border-strong)',
            background: 'var(--bg-elevated)',
            color: 'var(--text-primary)',
            fontFamily: 'inherit',
          }}
        />
        <span aria-live="polite" style={{ fontSize: 'var(--text-body-size)', color: 'var(--text-tertiary)' }}>
          {entries.slice(-6).join('  ')}
        </span>
      </div>
    );
  };
  return { id: 'delay', durationMs: delayMs, View };
}

// Reads stimulus.item and NOTHING else. It is handed no params, no block and no
// history, so it cannot tell whether this word was studied — the invariant is
// structural rather than a matter of care. `params.study_list` IS the answer
// key: an item is old exactly when it appears there, so re-rendering it turns
// every judgement into a lookup, and the server cannot tell the difference.
//
// The accessible name says which word is on screen, which is exactly what a
// sighted participant is given; it must never say whether that word was
// studied.
function StimulusView({ stimulus }) {
  return (
    <span role="img" aria-label={`the word ${stimulus.item}`} style={wordStyle}>
      {stimulus.item}
    </span>
  );
}

export const delayedRecognitionTask = {
  id: 'delayed-recognition',
  name: 'Seen Before?',
  pillar: 'memory',

  // Forced choice: every trial ends in a response, so trial length carries no
  // information about what the participant did and there is no rhythm to leak.
  // The trial may end as soon as an answer lands.
  holdsFullWindow: false,

  instructions: [
    'You will be shown a short list of words to remember.',
    'Then a counting task, to keep the list from staying in your head.',
    'Then words appear one at a time — say whether each was on the list.',
  ],
  // Negative LEFT, affirmative RIGHT — the battery-wide convention, so a
  // participant moving between instruments never has to re-learn which side
  // means "yes". ResponseTargets renders this array left-to-right with no
  // reordering, so the keys and hint belong to the POSITION, not to the id:
  // ArrowLeft/f must always sit on the left button whatever it answers.
  targets: [
    { id: 'new', label: 'Not on it', keys: ['ArrowLeft', 'f'], hint: 'F · ←' },
    { id: 'old', label: 'On the list', keys: ['ArrowRight', 'j'], hint: 'J · →' },
  ],

  // Empty deliberately. TaskExamples renders one stimulus per row on the
  // pre-run card, and any example word here would either be meaningless (this
  // task's lesson is a relation between a word and a list studied minutes
  // earlier) or, worse, an actual list word displayed outside its phase.
  examples: [],

  toAnswer: (targetId) => ({ isOld: targetId === 'old' }),

  // Before trial 0: the two-word practice list. Familiarisation only.
  preamble: (params) => {
    const words = wordListOf(params?.practice_study_list);
    if (words.length === 0) return [];
    return [
      studyPhase('practice-study', words, Number(params?.study_item_ms) || DEFAULT_STUDY_ITEM_MS),
    ];
  },

  // Before the first SCORED trial: the ten-word study list, then the filled
  // delay. The index is derived from the block rather than hardcoded, because
  // practiceTrialCount has already changed once elsewhere in this battery.
  preambleBefore: (trialIndex, params, blockTrials) => {
    const firstScored = (blockTrials || []).findIndex((t) => !t.isPractice);
    if (firstScored < 0 || trialIndex !== firstScored) return [];
    const words = wordListOf(params?.study_list);
    if (words.length === 0) return [];
    return [
      studyPhase('study', words, Number(params?.study_item_ms) || DEFAULT_STUDY_ITEM_MS),
      delayPhase(Number(params?.delay_ms) || DEFAULT_DELAY_MS),
    ];
  },

  // fixationMs 400 and gapMs 500 are this instrument's own convention, stated
  // in its brief. They are deliberately NOT harmonised with flanker/stroop
  // (fixation drawn inside the served isi) or n-back (a continuous stream with
  // neither): a recognition judgement is untimed relative to any isi, and the
  // gap is what separates one judgement from the next.
  timing: (params) => ({
    fixationMs: 400,
    windowMs: Number(params?.response_window_ms) || DEFAULT_WINDOW_MS,
    gapMs: 500,
  }),

  // PRACTICE ONLY. The runner gates this on trial.isPractice under an
  // assessment driver (feedbackOnEveryTrial: false), so it can never reach a
  // scored trial. This is the only function in the module that may read a study
  // list, and it returns a target id rather than any word — nothing from the
  // list reaches the DOM through it. It reads the PRACTICE list, never the main
  // one: at practice time the participant has not studied the main list yet, so
  // a main-list word is correctly "new".
  practiceExpectation: (stimulus, ctx) => {
    const studied = ctx?.params?.practice_study_list;
    if (!Array.isArray(studied)) return null;
    return studied.includes(stimulus.item) ? 'old' : 'new';
  },

  StimulusView,
};
